(function (window, document) {
  'use strict';

  var STORAGE_KEY = 'pa_attribution_v2';
  var SESSION_KEY = 'pa_session_v2';
  var OWN_KEYS = ['pa_attribution_v1', 'pa_session_v1', STORAGE_KEY, SESSION_KEY];
  var SCHEMA_VERSION = 2;
  var config = (window.__TRACKING_CONFIG__ || {}).attribution || {};
  var SESSION_TIMEOUT = positiveNumber(config.session_timeout_minutes, 30) * 60 * 1000;
  var TOUCH_TTL = positiveNumber(config.touch_ttl_days, 90) * 24 * 60 * 60 * 1000;
  var TRACKING_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content',
    'ref', 'gclid', 'fbclid', 'msclkid', 'ttclid'
  ];
  var lastPermission = null;
  var memory = null;
  var memorySession = null;
  var pageIdentity = null;
  var lastCapturedUrl = null;
  var storageClearedElsewhere = false;
  var legacyCleaned = false;
  var persistPending = false;
  var sharedIdentityNeedsRead = false;

  function positiveNumber(value, fallback) {
    var number = Number(value);
    return isFinite(number) && number > 0 ? number : fallback;
  }

  function id(prefix) {
    return prefix + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }

  function permission() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return null;
      var allowed = privacy.analyticsProcessingAllowed();
      return allowed === true ? true : (allowed === false ? false : null);
    } catch (error) {
      return null;
    }
  }

  function marketingPermission() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function forgetMemory() {
    memory = null;
    memorySession = null;
    // Keep the page-context boundary, not analytics IDs, across consent changes.
    // Otherwise an old authenticated page could replace a newer page's identity.
    lastCapturedUrl = null;
  }

  function removeKeys(keys) {
    keys.forEach(function (key) {
      try { window.localStorage.removeItem(key); } catch (error) {}
    });
  }

  function checkPermission() {
    var allowed = permission();
    if (allowed !== true) {
      // Removal is limited to this module's keys; it does not read stored analytics.
      if (lastPermission === true || (allowed === false && lastPermission !== false)) {
        // A failed removal must never allow the old record to return on re-agreement.
        persistPending = true;
        removeKeys(OWN_KEYS);
      }
      forgetMemory();
      lastPermission = allowed;
      return false;
    }
    lastPermission = true;
    if (!legacyCleaned) {
      // Legacy records lack consent, identity and session-expiry boundaries.
      removeKeys(OWN_KEYS.slice(0, 2));
      legacyCleaned = true;
    }
    return true;
  }

  function readJson(key, fallback) {
    try {
      return JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch (error) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function readSharedState() {
    try {
      return {
        ok: true,
        data: JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'),
        session: JSON.parse(window.localStorage.getItem(SESSION_KEY) || 'null')
      };
    } catch (error) {
      return { ok: false };
    }
  }

  function identityScope() {
    var context = window.__TRACKING_CONTEXT__ || {};
    var customerId = Object.prototype.hasOwnProperty.call(context, 'customer_id')
      ? context.customer_id : context.customer && context.customer.id;
    return customerId === null || typeof customerId === 'undefined' || customerId === ''
      ? 'guest' : 'customer:' + String(customerId);
  }

  function internalRef(value) {
    return typeof value === 'string' && /^(rbi|pb)$/i.test(value.trim());
  }

  function cleanParams(source) {
    var output = {};
    TRACKING_PARAMS.forEach(function (key) {
      var value = source && source[key];
      if (typeof value !== 'string' || !value || (key === 'ref' && internalRef(value))) return;
      output[key] = value;
    });
    return output;
  }

  function urlDetails(value, allowMarketing) {
    try {
      if (typeof value !== 'string' || !value) return { url: '', params: {} };
      var url = new URL(value, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return { url: '', params: {} };
      var candidates = {};
      if (allowMarketing === true) {
        TRACKING_PARAMS.forEach(function (key) { candidates[key] = url.searchParams.get(key); });
      }
      var params = allowMarketing === true ? cleanParams(candidates) : {};
      var safeUrl = new URL(url.origin + url.pathname);
      TRACKING_PARAMS.forEach(function (key) {
        if (params[key]) safeUrl.searchParams.set(key, params[key]);
      });
      return { url: safeUrl.href, params: params };
    } catch (error) {
      return { url: '', params: {} };
    }
  }

  function hasTracking(params) {
    return Object.keys(params || {}).length > 0;
  }

  function sourceLinkId(params) {
    // Preserve the established UTM key format; do not infer a channel from referrer.
    return [params.utm_source || 'unknown', params.utm_medium || 'unknown',
      params.utm_campaign || 'unknown', params.utm_id || params.utm_term || '',
      params.utm_content || ''].filter(Boolean).join('|').toLowerCase();
  }

  function validTime(value, now, ttl) {
    var time = typeof value === 'number' ? value : Date.parse(value);
    return isFinite(time) && time <= now && now - time < ttl;
  }

  function cleanTouch(touch, now) {
    if (!touch || !validTime(touch.captured_at, now, TOUCH_TTL)) return null;
    var params = cleanParams(touch);
    if (!hasTracking(params)) return null;
    return Object.assign(params, {
      source_link_id: sourceLinkId(params),
      page_url: urlDetails(touch.page_url || '', true).url,
      captured_at: touch.captured_at
    });
  }

  function validData(data, scope, now) {
    return data && data.schema_version === SCHEMA_VERSION && data.identity_scope === scope &&
      typeof data.pa_anon_id === 'string' && data.pa_anon_id &&
      typeof data.expires_at === 'number' && data.expires_at > now &&
      data.expires_at <= now + TOUCH_TTL;
  }

  function validSession(session, scope, now) {
    return session && session.schema_version === SCHEMA_VERSION && session.identity_scope === scope &&
      typeof session.pa_session_id === 'string' && session.pa_session_id &&
      typeof session.last_activity_at === 'number' &&
      validTime(session.last_activity_at, now, SESSION_TIMEOUT) &&
      isFinite(Date.parse(session.started_at)) &&
      Date.parse(session.started_at) <= session.last_activity_at;
  }

  function update(capture) {
    if (!checkPermission() || storageClearedElsewhere) return {};
    var now = Date.now();
    var scope = identityScope();
    var allowMarketing = marketingPermission();
    var shared = sharedIdentityNeedsRead ? readSharedState() : null;
    if (shared && !shared.ok) return {};
    var existing = shared ? shared.data : readJson(STORAGE_KEY, memory);
    var session = shared ? shared.session : readJson(SESSION_KEY, memorySession);

    // An old tab must not restore an identity that another page has replaced.
    if ((pageIdentity === scope || sharedIdentityNeedsRead) && existing && existing.schema_version === SCHEMA_VERSION &&
        existing.identity_scope !== scope) {
      memory = null;
      memorySession = null;
      return {};
    }
    if (shared) sharedIdentityNeedsRead = false;

    // Until both writes succeed, cleared/sanitized page memory takes precedence
    // over a previous persisted record (including after marketing-only withdrawal).
    if (persistPending) {
      existing = memory;
      session = memorySession;
    }

    var current = urlDetails(window.location.href, allowMarketing);
    var captureCurrent = capture || lastCapturedUrl !== current.url;
    if (!validData(existing, scope, now)) {
      existing = {
        schema_version: SCHEMA_VERSION,
        identity_scope: scope,
        pa_anon_id: id('paa'),
        first_landing_url: captureCurrent ? current.url : urlDetails(window.location.href, false).url,
        first_touch_at: new Date(now).toISOString(),
        expires_at: now + TOUCH_TTL
      };
      session = null;
    } else {
      // Rebuild the record from known fields, so old or injected URL data is not retained.
      existing = {
        schema_version: SCHEMA_VERSION,
        identity_scope: scope,
        pa_anon_id: existing.pa_anon_id,
        first_landing_url: urlDetails(existing.first_landing_url || '', allowMarketing).url,
        first_touch_at: existing.first_touch_at,
        first_touch: allowMarketing ? cleanTouch(existing.first_touch, now) : null,
        latest_touch: allowMarketing ? cleanTouch(existing.latest_touch, now) : null,
        expires_at: existing.expires_at
      };
      if (!existing.first_touch && existing.latest_touch) existing.first_touch = existing.latest_touch;
      if (!validTime(existing.first_touch_at, now, TOUCH_TTL)) {
        existing.first_landing_url = existing.first_touch ? existing.first_touch.page_url : current.url;
        existing.first_touch_at = existing.first_touch ? existing.first_touch.captured_at : new Date(now).toISOString();
      }
    }

    if (!validSession(session, scope, now)) {
      session = {
        schema_version: SCHEMA_VERSION,
        identity_scope: scope,
        pa_session_id: id('pas'),
        started_at: new Date(now).toISOString()
      };
    }
    session.last_activity_at = now;

    if (captureCurrent && hasTracking(current.params)) {
      var touch = Object.assign({}, current.params, {
        source_link_id: sourceLinkId(current.params),
        page_url: current.url,
        captured_at: new Date(now).toISOString()
      });
      if (!existing.first_touch) existing.first_touch = touch;
      existing.latest_touch = touch;
      existing.expires_at = now + TOUCH_TTL;
    }

    existing.pa_session_id = session.pa_session_id;
    pageIdentity = scope;
    lastCapturedUrl = current.url;
    memory = existing;
    memorySession = session;
    var sessionWritten = writeJson(SESSION_KEY, session);
    var attributionWritten = writeJson(STORAGE_KEY, existing);
    persistPending = !sessionWritten || !attributionWritten;
    return existing;
  }

  function flatten(data) {
    if (!data.pa_anon_id) return {};
    var allowMarketing = marketingPermission();
    var first = (allowMarketing && data.first_touch) || {};
    var latest = (allowMarketing && data.latest_touch) || {};
    return {
      pa_anon_id: data.pa_anon_id,
      pa_session_id: data.pa_session_id,
      first_landing_url: urlDetails(data.first_landing_url, allowMarketing).url,
      first_touch_at: data.first_touch_at,
      source_link_id: latest.source_link_id || first.source_link_id,
      first_utm_source: first.utm_source,
      first_utm_medium: first.utm_medium,
      first_utm_campaign: first.utm_campaign,
      first_utm_id: first.utm_id,
      first_utm_term: first.utm_term,
      first_utm_content: first.utm_content,
      latest_utm_source: latest.utm_source,
      latest_utm_medium: latest.utm_medium,
      latest_utm_campaign: latest.utm_campaign,
      latest_utm_id: latest.utm_id,
      latest_utm_term: latest.utm_term,
      latest_utm_content: latest.utm_content,
      latest_ref: latest.ref,
      current_page_url: urlDetails(window.location.href, allowMarketing).url
    };
  }

  window.ProtoArcAttribution = {
    version: '2.0.0',
    get: function () { return flatten(update(false)); },
    refresh: function () { return flatten(update(false)); },
    isIdentityCurrent: function () {
      if (!checkPermission() || storageClearedElsewhere) return false;
      var scope = identityScope();
      var now = Date.now();
      var shared = sharedIdentityNeedsRead ? readSharedState() : null;
      if (shared && !shared.ok) return false;
      return pageIdentity === scope &&
        !!validData(shared ? shared.data : readJson(STORAGE_KEY, memory), scope, now) &&
        !!validSession(shared ? shared.session : readJson(SESSION_KEY, memorySession), scope, now);
    }
  };

  document.addEventListener('visitorConsentCollected', function () {
    // Read the authoritative API, never trust event detail as a permission grant.
    storageClearedElsewhere = false;
    update(false);
  });
  document.addEventListener('tracking:privacy-ready', function () { update(false); });
  ['pointerdown', 'keydown'].forEach(function (name) {
    document.addEventListener(name, function () { update(false); }, { passive: true });
  });
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY && event.key !== SESSION_KEY && event.key !== null) return;
    // After another tab changes state, page-memory fallback cannot prove identity.
    sharedIdentityNeedsRead = true;
    memory = null;
    memorySession = null;
    // A cross-tab clear is a boundary, not an instruction to recreate identifiers.
    if (event.key === null || (event.key === STORAGE_KEY && event.newValue === null)) {
      storageClearedElsewhere = true;
    } else if (event.key === STORAGE_KEY && event.newValue !== null) {
      storageClearedElsewhere = false;
    }
  });

  update(true);
})(window, document);
